/*
<purpose>
  THE identity facade — the single point where the frontend decides HOW a request
  authenticates: an Entra access token (msalAuth.ts) as Authorization: Bearer. Every API client,
  the App-level user lookup, and both interactive doors (sign in, sign out) consume THIS module,
  never msalAuth directly, so the header NAME decision exists exactly once and a call site added
  later cannot bypass it.
</purpose>
<dependencies>msalAuth.ts (MSAL via the lazy getters in authConfig.ts).</dependencies>
<constraints>
  Single dialect: Bearer only. The retired principal-header path (a header the browser
  reconstructed itself, unsigned) must not come back as a fallback — the backend never reads it.
  Within src/, the retired token names live in exactly one place — the retired-axis guard test in
  __tests__/, which scans src/ (case-insensitively) and reds on a reappearance. Naming them here
  too would be a second home inside the scanned tree that the scan would then have to exempt; that
  is also why this paragraph does not spell the guard's own file name. The token names that must
  not return are in smarttix-receiver.md § "Auth", outside the scanned tree on purpose.
</constraints>
*/

import { acquireFaToken, invalidateCachedFaToken, fetchMsalUser, beginMsalLogin, beginMsalLogout } from './msalAuth';

export interface CurrentUser {
  email: string;
  name: string;
  userId: string;
}

export interface AuthHeader {
  name: string;
  value: string;
}

// Why a data-shaped return instead of mutating axios config here: both API clients (api.ts,
// chatbotApi.ts) and the chatbot's manual-headers path attach headers in their own shapes;
// returning {name, value} keeps this module transport-agnostic and each call site one line.
export const getAuthHeader = async (): Promise<AuthHeader | null> => {
  const token = await acquireFaToken();
  return token ? { name: 'Authorization', value: `Bearer ${token}` } : null;
};

// Why: a 401 means the token MSAL just handed us was rejected server-side even though its local
// expiry check said it was good — the next acquisition must not hand back the same cache hit.
export const onAuthRejected = (): void => { invalidateCachedFaToken(); };

export const fetchCurrentUser = async (): Promise<CurrentUser | null> => fetchMsalUser();

// Why here: the login BUTTON and the 401 recovery both open the MSAL door through the facade,
// preserving the current page as the post-login return (msalAuth owns the redirect).
export const beginInteractiveLogin = async (): Promise<void> => { await beginMsalLogin(); };

// Why the facade owns the way OUT as well as the way IN: signing out means ending the session this
// app actually holds — the MSAL account cache. Every Abmelden control routes here so no call site
// can navigate to a door that looks like a logout but leaves that cache untouched.
export const signOut = async (): Promise<void> => { await beginMsalLogout(); };
